import React from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { Breadcrumb } from '../../components/Breadcrumb';

type EditorPanelProps = {
  activeFile: string | null;
  openTabs: string[];
  code: string;
  editorHeight: number;
  status: string;
  lintSummary: { errors: number; warnings: number };
  indentSize: number;
  encoding: string;
  editorExtensions: Extension[];
  theme: 'dark' | 'light';
  isDirty: (path: string) => boolean;
  resolvePath: (id: string) => string;
  onSelectTab: (id: string) => void;
  onCloseTab: (id: string) => void;
  onChange: (value: string) => void;
  onBlur: () => void;
  onCreateEditor: (view: EditorView) => void;
  onStartResize: (clientY: number) => void;
  onChangeIndent: (n: number) => void;
  onChangeEncoding: (val: string) => void;
  onBreadcrumbNavigate?: (path: string) => void;
};

export const EditorPanel = React.memo(function EditorPanel({
  activeFile,
  openTabs,
  code,
  editorHeight,
  status,
  lintSummary,
  indentSize,
  encoding,
  editorExtensions,
  theme,
  isDirty,
  resolvePath,
  onSelectTab,
  onCloseTab,
  onChange,
  onBlur,
  onCreateEditor,
  onStartResize,
  onChangeIndent,
  onChangeEncoding,
  onBreadcrumbNavigate,
}: EditorPanelProps) {
  // Get the full path for breadcrumb
  const activePath = activeFile ? resolvePath(activeFile) : '';
  return (
    <section className="editor">
      <div className="tab-bar">
        {openTabs.map((tab) => (
          <div
            key={tab}
            className={`tab ${isDirty(tab) ? 'modified' : 'clean'} ${tab === activeFile ? 'active' : ''}`}
            onClick={() => onSelectTab(tab)}
          >
            <span className="tab-dot" aria-hidden />
            <span>{resolvePath(tab)}</span>
            {isDirty(tab) && <span className="tab-pill">●</span>}
            <button
              className="tab-close"
              onClick={(event) => {
                event.stopPropagation();
                onCloseTab(tab);
              }}
              aria-label={`Close ${tab}`}
            >
              ×
            </button>
          </div>
        ))}
        <button className="micro add">+</button>
      </div>
      {activePath && (
        <Breadcrumb filePath={activePath} onNavigate={onBreadcrumbNavigate} />
      )}
      <div className="code" style={{ height: `${editorHeight}px` }}>
        {activeFile ? (
          <CodeMirror
            value={code}
            height="100%"
            theme={theme === 'dark' ? oneDark : 'light'}
            extensions={editorExtensions}
            onChange={onChange}
            onBlur={onBlur}
            onCreateEditor={onCreateEditor}
            basicSetup={{
              lineNumbers: false,
              foldGutter: false,
              highlightActiveLine: true,
            }}
          />
        ) : (
          <div className="empty-editor">
            <p className="eyebrow">API</p>
            <h3>Dosyalar yükleniyor…</h3>
            <p className="muted">Env ayarlarını yapıp endpointlere erişince ağaç burada dolacak.</p>
          </div>
        )}
      </div>
      <div
        className="editor-resizer"
        onMouseDown={(e) => {
          onStartResize(e.clientY);
          document.body.style.cursor = 'row-resize';
        }}
      />
      <div className="status-bar">
        <div className="status-item">
          <span className="status-led" />
          {status}
        </div>
        <div className="status-item">Lint: {lintSummary.errors} errors, {lintSummary.warnings} warnings</div>
        <div className="status-item">TypeScript + JSX</div>
        <div className="status-item">Spaces: {indentSize}</div>
        <div className="status-item">{encoding}</div>
        <div className="status-item">
          <label className="status-label">Indent</label>
          <select className="status-select" value={indentSize} onChange={(e) => onChangeIndent(parseInt(e.target.value, 10) || 2)}>
            {[2, 4, 8].map((n) => (
              <option key={n} value={n}>
                {n} spaces
              </option>
            ))}
          </select>
        </div>
        <div className="status-item">
          <label className="status-label">Encoding</label>
          <select className="status-select" value={encoding} onChange={(e) => onChangeEncoding(e.target.value)}>
            <option value="UTF-8">UTF-8</option>
            <option value="ISO-8859-1">ISO-8859-1</option>
            <option value="ASCII">ASCII</option>
          </select>
        </div>
      </div>
    </section>
  );
});
